import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, RefreshCw, Ship, Hammer, Mail, Phone, MessageSquare, Trash2, Download, Truck, MapPin, TrendingUp, Users, Sparkles, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_META = {
  barge: { label: 'Barge', icon: Ship, color: 'text-cyan-300', ring: 'border-cyan-400/40 bg-cyan-500/10' },
  hammer: { label: 'Hammer', icon: Hammer, color: 'text-amber-300', ring: 'border-amber-400/40 bg-amber-500/10' },
};

const STATUS_META = {
  new: { label: 'New', class: 'bg-cyan-500/15 text-cyan-300 border-cyan-400/30' },
  contacted: { label: 'Contacted', class: 'bg-amber-500/15 text-amber-200 border-amber-400/30' },
  closed: { label: 'Closed', class: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30' },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function formatEstimate(e) {
  if (!e) return null;
  const hull = e.model === '10x30' ? "10' × 30'" : e.model === '8x24' ? "8' × 24'" : e.model;
  const dest = e.dest_city ? `${e.dest_city}, ${e.dest_state}` : `ZIP ${e.zip}`;
  const range = e.low != null && e.high != null
    ? `$${Number(e.low).toLocaleString()} – $${Number(e.high).toLocaleString()}`
    : null;
  return { hull, dest, range, miles: e.distance_miles, at: e.at, zip: e.zip };
}

function toCsv(leads) {
  const header = ['created_at', 'source', 'status', 'name', 'phone', 'email', 'sms_consent', 'message'];
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const rows = leads.map((l) =>
    [l.created_at, l.source, l.status, l.name, l.phone, l.email, l.sms_consent, l.message].map(escape).join(',')
  );
  return [header.join(','), ...rows].join('\n');
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const adminEmail = typeof window !== 'undefined' ? localStorage.getItem('admin_email') : '';

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [estStats, setEstStats] = useState(null);
  const [tab, setTab] = useState('leads');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pricedOnly, setPricedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (pricedOnly) params.priced_only = true;
      const [leadsRes, statsRes, estRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers: authHeader, params }),
        axios.get(`${API}/leads/stats`, { headers: authHeader }),
        axios.get(`${API}/estimator/stats`, { headers: authHeader }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setEstStats(estRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader, sourceFilter, statusFilter, pricedOnly, logout]);

  useEffect(() => {
    document.title = 'Admin Dashboard · Absolute Marine Equipment';
    if (!token) {
      navigate('/admin/login', { replace: true });
      return;
    }
    load();
  }, [token, load, navigate]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/leads/${id}`, { status }, { headers: authHeader });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
    } catch (e) {
      // no-op
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: authHeader });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) {
      // no-op
    }
  };

  const exportCsv = () => {
    const csv = toCsv(leads);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#050608]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Absolute Marine Equipment</div>
            <h1 className="font-display text-white text-lg sm:text-xl font-semibold">Leads Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-zinc-500">{adminEmail}</span>
            <button
              onClick={load}
              className="btn-ghost px-3 py-2 rounded-md text-xs inline-flex items-center gap-2"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              onClick={logout}
              className="btn-ghost px-3 py-2 rounded-md text-xs inline-flex items-center gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-white/10" data-testid="admin-tabs">
          <TabButton active={tab === 'leads'} onClick={() => setTab('leads')} testId="tab-leads">
            <Users className="w-3.5 h-3.5" /> Leads
          </TabButton>
          <TabButton active={tab === 'estimator'} onClick={() => setTab('estimator')} testId="tab-estimator">
            <Truck className="w-3.5 h-3.5" /> Estimator
          </TabButton>
        </div>

        {tab === 'leads' && (
          <>
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8" data-testid="admin-stats">
            <StatCard label="Total leads" value={stats.total} />
            <StatCard label="Barge" value={stats.by_source.barge} accent="text-cyan-300" />
            <StatCard label="Hammer" value={stats.by_source.hammer} accent="text-amber-300" />
            <StatCard label="New" value={stats.by_status.new} accent="text-cyan-300" />
            <StatCard label="Closed" value={stats.by_status.closed} accent="text-zinc-400" />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-5 items-center">
          <FilterGroup
            label="Source"
            value={sourceFilter}
            onChange={setSourceFilter}
            options={[
              { v: 'all', l: 'All' },
              { v: 'barge', l: 'Barge' },
              { v: 'hammer', l: 'Hammer' },
            ]}
            testId="filter-source"
          />
          <FilterGroup
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { v: 'all', l: 'All' },
              { v: 'new', l: 'New' },
              { v: 'contacted', l: 'Contacted' },
              { v: 'closed', l: 'Closed' },
            ]}
            testId="filter-status"
          />
          <button
            type="button"
            onClick={() => setPricedOnly((v) => !v)}
            data-testid="filter-priced-only"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs uppercase tracking-[0.16em] transition-colors ${
              pricedOnly
                ? 'border-cyan-400/50 bg-cyan-500/15 text-cyan-200'
                : 'border-white/10 bg-white/5 text-zinc-400 hover:text-white'
            }`}
            title="Show only leads that ran a shipping estimate first"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Priced first
          </button>
          <div className="ml-auto">
            <button
              onClick={exportCsv}
              disabled={!leads.length}
              className="btn-teal px-4 py-2 rounded-md text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="admin-export-csv"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>

        {/* Leads table */}
        <div className="panel rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-white/5 text-zinc-400 uppercase text-[10px] tracking-[0.2em]">
                <tr>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Contact</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Message</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Received</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="text-center py-10 text-zinc-500">Loading leads…</td></tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-zinc-500" data-testid="admin-empty-state">
                      No leads match these filters yet.
                    </td>
                  </tr>
                )}
                {!loading && leads.map((l) => {
                  const meta = SOURCE_META[l.source] || SOURCE_META.barge;
                  const Icon = meta.icon;
                  return (
                    <tr
                      key={l.id}
                      className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer"
                      onClick={() => setSelected(l)}
                      data-testid={`lead-row-${l.id}`}
                    >
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs ${meta.ring} ${meta.color}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-white">{l.name}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-zinc-300">
                        <div className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-zinc-500" />{l.phone}</div>
                        {l.email && <div className="flex items-center gap-1.5 text-xs text-zinc-500"><Mail className="w-3 h-3" />{l.email}</div>}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-zinc-400 max-w-xs truncate">
                        {l.message}
                        {l.last_estimate && (
                          <div className="mt-1 inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded border border-cyan-400/30 bg-cyan-500/10 text-[10px] uppercase tracking-[0.12em] text-cyan-300" data-testid={`lead-estimate-chip-${l.id}`}>
                            <Sparkles className="w-2.5 h-2.5" />
                            Priced shipping · ZIP {l.last_estimate.zip}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 rounded border text-[11px] uppercase tracking-[0.14em] ${STATUS_META[l.status].class}`}>
                          {STATUS_META[l.status].label}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-zinc-500 text-xs">{formatDate(l.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); removeLead(l.id); }}
                          className="text-zinc-500 hover:text-red-400"
                          data-testid={`lead-delete-${l.id}`}
                          title="Delete lead"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
          </>
        )}

        {tab === 'estimator' && (
          <>
            <EstimatorView data={estStats} loading={loading} />
            <HammerEstimatorInsights authHeader={authHeader} />
          </>
        )}
      </main>

      {/* Lead detail slide-over */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setSelected(null)}>
          <div className="flex-1 bg-black/70 backdrop-blur-sm" />
          <aside
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:w-[420px] bg-[#0a0c10] border-l border-white/10 h-full overflow-y-auto p-6"
            data-testid="lead-detail-panel"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-1">Lead detail</div>
                <h2 className="font-display text-white text-xl font-semibold">{selected.name}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="text-zinc-500 hover:text-white" data-testid="lead-detail-close">✕</button>
            </div>

            <div className="space-y-4 text-sm">
              <DetailRow icon={<Phone className="w-4 h-4" />} label="Phone">
                <a href={`tel:${selected.phone}`} className="text-cyan-300 hover:text-cyan-200">{selected.phone}</a>
              </DetailRow>
              {selected.email && (
                <DetailRow icon={<Mail className="w-4 h-4" />} label="Email">
                  <a href={`mailto:${selected.email}`} className="text-cyan-300 hover:text-cyan-200 break-all">{selected.email}</a>
                </DetailRow>
              )}
              <DetailRow icon={<MessageSquare className="w-4 h-4" />} label="Message">
                <p className="text-zinc-200 whitespace-pre-wrap">{selected.message || '(none)'}</p>
              </DetailRow>

              {selected.last_estimate && (
                <div className="rounded-lg border border-cyan-400/30 bg-cyan-500/[0.06] p-4" data-testid="lead-estimate-detail">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
                    <span className="text-[10px] uppercase tracking-[0.24em] text-cyan-300 font-semibold">Priced their shipping first</span>
                  </div>
                  {(() => {
                    const e = formatEstimate(selected.last_estimate);
                    return (
                      <>
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <span className="font-display text-white text-lg font-bold whitespace-nowrap">{e.range || '—'}</span>
                          <span className="text-[10px] uppercase tracking-[0.16em] text-cyan-200">{e.hull}</span>
                        </div>
                        <div className="text-[11px] text-zinc-400">
                          to {e.dest}{e.miles != null ? ` · ${e.miles} mi from Fort Myers` : ''}
                        </div>
                        {e.at && (
                          <div className="text-[10px] text-zinc-500 mt-1">
                            Priced {formatDate(e.at)}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <MetaBox label="Source">{SOURCE_META[selected.source]?.label || selected.source}</MetaBox>
                <MetaBox label="Received">{formatDate(selected.created_at)}</MetaBox>
                <MetaBox label="SMS consent">{selected.sms_consent ? 'Yes' : 'No'}</MetaBox>
                <MetaBox label="Status">{STATUS_META[selected.status].label}</MetaBox>
              </div>

              <div className="pt-4">
                <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Update status</div>
                <div className="flex gap-2">
                  {['new', 'contacted', 'closed'].map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(selected.id, s)}
                      className={`flex-1 px-3 py-2 rounded-md text-xs uppercase tracking-[0.14em] border ${
                        selected.status === s
                          ? `${STATUS_META[s].class}`
                          : 'border-white/10 text-zinc-400 hover:border-white/30'
                      }`}
                      data-testid={`lead-status-${s}`}
                    >
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent = 'text-white' }) {
  return (
    <div className="panel rounded-lg border border-white/10 px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-semibold ${accent}`}>{value}</div>
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors ${
        active
          ? 'border-cyan-400 text-cyan-300'
          : 'border-transparent text-zinc-500 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function EstimatorView({ data, loading }) {
  if (loading && !data) {
    return <div className="text-center py-16 text-zinc-500">Loading estimator activity…</div>;
  }
  if (!data || data.total === 0) {
    return (
      <div className="panel rounded-xl border border-white/10 p-10 text-center" data-testid="estimator-empty-state">
        <Truck className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
        <h3 className="font-display text-white text-lg font-semibold mb-1">No estimates run yet</h3>
        <p className="text-zinc-500 text-sm max-w-md mx-auto">
          Once buyers use the ZIP-code shipping estimator, their ZIPs, hull picks, and computed ranges will show up here.
        </p>
      </div>
    );
  }
  const hullTotal = data.by_hull['8x24'] + data.by_hull['10x30'];
  const widePct = hullTotal ? Math.round((data.by_hull['10x30'] / hullTotal) * 100) : 0;
  return (
    <div data-testid="estimator-view">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
        <StatCard label="Total estimates" value={data.total} />
        <StatCard label="Priced leads" value={data.priced_leads} accent="text-cyan-300" />
        <StatCard label="Conversion" value={`${data.conversion_pct}%`} accent="text-emerald-300" />
        <StatCard label="Rescued by nudge" value={data.nudge_rescues ?? 0} accent="text-fuchsia-300" />
        <StatCard label={`10ft × 30ft · ${widePct}% wide-load`} value={data.by_hull['10x30']} accent="text-amber-300" />
      </div>

      {/* Conversion insight banner */}
      <div className="mb-6 rounded-lg border border-emerald-400/25 bg-emerald-500/[0.06] px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="conversion-banner">
        <TrendingUp className="w-4 h-4 text-emerald-300" />
        <span className="text-white">
          <span className="font-semibold text-emerald-300">{data.priced_leads}</span>
          <span className="text-zinc-400"> of </span>
          <span className="font-semibold">{data.total}</span>
          <span className="text-zinc-400"> estimates converted to a lead</span>
        </span>
        <span className="text-zinc-600">·</span>
        <span className="text-zinc-400">
          Last 7 days:{' '}
          <span className="text-emerald-300 font-semibold">{data.conversion_pct_7_days}%</span>
          <span className="text-zinc-600"> ({data.priced_leads_7_days}/{data.last_7_days})</span>
        </span>
        {(data.nudge_rescues ?? 0) > 0 && (
          <>
            <span className="text-zinc-600">·</span>
            <span className="text-zinc-400">
              Nudge rescued{' '}
              <span className="text-fuchsia-300 font-semibold">{data.nudge_rescues}</span>
              <span className="text-zinc-500"> ({data.nudge_rescue_pct}% of priced leads)</span>
            </span>
            {data.nudge_variants && Object.keys(data.nudge_variants).length > 0 && (
              <span className="text-zinc-500 text-[11px]" data-testid="nudge-variant-breakdown">
                &nbsp;·&nbsp;
                {Object.entries(data.nudge_variants).map(([id, count], i) => (
                  <span key={id}>
                    {i > 0 && ' · '}
                    <span className="uppercase tracking-[0.14em] text-fuchsia-200/70">{id}</span>
                    <span className="text-fuchsia-300 font-semibold ml-1">{count}</span>
                  </span>
                ))}
              </span>
            )}
          </>
        )}
        <span className="text-zinc-600">·</span>
        <span className="text-zinc-500 text-[11px]">
          8ft × 24ft: <span className="text-zinc-300">{data.by_hull['8x24']}</span>
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Top ZIPs */}
        <div className="lg:col-span-3 panel rounded-xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-cyan-300" />
            <h3 className="font-display text-white text-sm font-semibold uppercase tracking-[0.18em]">Top ZIP codes</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="estimator-top-zips">
              <thead className="bg-white/5 text-zinc-400 uppercase text-[10px] tracking-[0.2em]">
                <tr>
                  <th className="text-left px-4 py-2">#</th>
                  <th className="text-left px-4 py-2">ZIP · destination</th>
                  <th className="text-right px-4 py-2">Mi</th>
                  <th className="text-right px-4 py-2">8×24</th>
                  <th className="text-right px-4 py-2">10×30</th>
                  <th className="text-right px-4 py-2">Avg range</th>
                  <th className="text-right px-4 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.top_zips.map((z, i) => (
                  <tr key={z.zip} className="border-t border-white/5" data-testid={`top-zip-${z.zip}`}>
                    <td className="px-4 py-2.5 text-zinc-500">{i + 1}</td>
                    <td className="px-4 py-2.5">
                      <div className="text-white font-medium">{z.zip}</div>
                      <div className="text-[11px] text-zinc-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {z.dest_city ? `${z.dest_city}, ${z.dest_state}` : '—'}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right text-zinc-400 tabular-nums">{z.distance_miles}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-300 tabular-nums">{z.hull_8x24}</td>
                    <td className="px-4 py-2.5 text-right text-amber-300 tabular-nums">{z.hull_10x30}</td>
                    <td className="px-4 py-2.5 text-right text-cyan-300 tabular-nums whitespace-nowrap">
                      ${z.avg_low.toLocaleString()} – ${z.avg_high.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-right text-white font-semibold tabular-nums">{z.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent activity */}
        <div className="lg:col-span-2 panel rounded-xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <Truck className="w-4 h-4 text-cyan-300" />
            <h3 className="font-display text-white text-sm font-semibold uppercase tracking-[0.18em]">Recent activity</h3>
          </div>
          <ul className="divide-y divide-white/5 max-h-[520px] overflow-y-auto" data-testid="estimator-recent">
            {data.recent.map((r) => (
              <li key={r.id} className="px-4 py-3 flex items-start gap-3">
                <div className={`w-1 h-9 rounded-full ${r.model === '10x30' ? 'bg-amber-400' : 'bg-cyan-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-white font-medium text-sm truncate">
                      {r.zip}
                      <span className="text-zinc-500 font-normal"> · {r.dest_city ? `${r.dest_city}, ${r.dest_state}` : '—'}</span>
                    </div>
                    <div className={`text-[10px] uppercase tracking-[0.18em] shrink-0 ${r.model === '10x30' ? 'text-amber-300' : 'text-cyan-300'}`}>
                      {r.model === '10x30' ? '10×30' : '8×24'}
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 mt-0.5">
                    <div className="text-[11px] text-zinc-500">{formatDate(r.created_at)}</div>
                    <div className="text-[11px] text-zinc-400 tabular-nums whitespace-nowrap">
                      ${r.low.toLocaleString()} – ${r.high.toLocaleString()}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, value, onChange, options, testId }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{label}</span>
      <div className="flex bg-white/5 border border-white/10 rounded-md p-0.5">
        {options.map((o) => (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={`px-3 py-1.5 rounded text-xs ${
              value === o.v ? 'bg-cyan-500/20 text-cyan-200' : 'text-zinc-400 hover:text-white'
            }`}
            data-testid={`${testId}-${o.v}`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function DetailRow({ icon, label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1 flex items-center gap-2">
        <span className="text-cyan-300">{icon}</span> {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function MetaBox({ label, children }) {
  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-0.5">{label}</div>
      <div className="text-zinc-200 text-xs">{children}</div>
    </div>
  );
}


const CARRIER_LABELS = {
  ltl_freight: 'LTL Freight (best-rate)',
  fedex_ground: 'FedEx Ground',
  ups_ground: 'UPS Ground',
  rl_ltl: 'R+L Carriers LTL',
};

function prettyCarrier(id) {
  return CARRIER_LABELS[id] || id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function fmtMoney(n) {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

// Small horizontal bar used by both the carrier and zone breakdowns.
function BreakdownBar({ label, count, total, tone = 'cyan' }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const fill = tone === 'amber' ? 'bg-amber-400/70' : 'bg-cyan-400/70';
  return (
    <div className="flex items-center gap-3">
      <div className="w-40 shrink-0 text-xs text-zinc-300 truncate">{label}</div>
      <div className="flex-1 h-2 rounded-full bg-white/[0.05] overflow-hidden">
        <div className={`h-full ${fill}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-14 text-right text-xs text-zinc-400 tabular-nums">
        <span className="text-white font-medium">{count}</span>
        <span className="text-zinc-600 ml-1">{pct}%</span>
      </div>
    </div>
  );
}

// "Estimator Insights" — proxied hammer-site stats. Mounted below the
// local EstimatorView. Owns its own fetch loop so failures here can't
// break the surrounding dashboard.
function HammerEstimatorInsights({ authHeader }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoaded, setLastLoaded] = useState(null);

  const fetchStats = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data: payload } = await axios.get(`${API}/admin/hammer-estimator-stats`, {
        headers: authHeader,
      });
      setData(payload);
      setError(null);
      setLastLoaded(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || 'Estimator stats unavailable right now');
    } finally {
      setRefreshing(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchStats();
    const id = setInterval(fetchStats, 60_000);
    return () => clearInterval(id);
  }, [fetchStats]);

  if (error && !data) {
    return (
      <div className="mt-12 panel rounded-xl border border-white/10 p-6 flex items-start gap-3" data-testid="hammer-insights-error">
        <AlertCircle className="w-5 h-5 text-zinc-500 mt-0.5" />
        <div className="flex-1">
          <div className="font-display text-white text-sm font-semibold mb-1">Hammer Shipping Estimator</div>
          <div className="text-zinc-500 text-sm">Estimator stats unavailable right now.</div>
        </div>
        <button
          type="button"
          onClick={fetchStats}
          disabled={refreshing}
          className="px-3 py-1.5 text-xs uppercase tracking-[0.16em] text-zinc-400 border border-white/10 rounded-md hover:text-white disabled:opacity-50"
          data-testid="hammer-insights-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mt-12 text-center py-10 text-zinc-500" data-testid="hammer-insights-loading">
        Loading hammer estimator stats…
      </div>
    );
  }

  const total = data.total || 0;
  const byCarrier = data.by_carrier || {};
  const byZone = data.by_zone || {};
  const carrierEntries = Object.entries(byCarrier).sort((a, b) => b[1] - a[1]);
  const zoneEntries = ['2', '3', '4', '5', '6', '7', '8']
    .map((z) => [z, byZone[z] || 0])
    .filter(([, c]) => c > 0);

  return (
    <div className="mt-14 pt-10 border-t border-white/5" data-testid="hammer-insights">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Hammer className="w-4 h-4 text-amber-300" />
            <h2 className="font-display text-white text-lg sm:text-xl font-semibold uppercase tracking-[0.14em]">
              Hammer Shipping Estimator
            </h2>
          </div>
          <div className="text-[11px] text-zinc-500">
            Live from absolutehammers.com · auto-refreshes every 60s
            {lastLoaded && <> · last loaded {relTime(lastLoaded.toISOString())}</>}
          </div>
        </div>
        <button
          type="button"
          onClick={fetchStats}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.16em] text-zinc-300 border border-white/10 rounded-md hover:text-white hover:border-cyan-400/40 transition-colors disabled:opacity-50"
          data-testid="hammer-insights-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="panel rounded-xl border border-white/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Total estimates</div>
          <div className="font-display text-white text-3xl font-semibold mt-1">{total}</div>
        </div>

        <div className="panel rounded-xl border border-white/10 p-5 lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-3">Carrier breakdown</div>
          {carrierEntries.length === 0 ? (
            <div className="text-zinc-500 text-sm">No carrier data yet.</div>
          ) : (
            <div className="space-y-2">
              {carrierEntries.map(([id, count]) => (
                <BreakdownBar key={id} label={prettyCarrier(id)} count={count} total={total} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Zone distribution */}
        <div className="lg:col-span-2 panel rounded-xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-amber-300" />
            <h3 className="font-display text-white text-sm font-semibold uppercase tracking-[0.18em]">
              Zone distribution
            </h3>
            <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-zinc-600">2 = FL · 8 = coast</span>
          </div>
          <div className="p-4 space-y-2" data-testid="hammer-zones">
            {zoneEntries.length === 0 ? (
              <div className="text-zinc-500 text-sm">No zone data yet.</div>
            ) : (
              zoneEntries.map(([z, count]) => (
                <BreakdownBar key={z} label={`Zone ${z}`} count={count} total={total} tone="amber" />
              ))
            )}
          </div>
        </div>

        {/* Top ZIPs */}
        <div className="lg:col-span-3 panel rounded-xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-amber-300" />
            <h3 className="font-display text-white text-sm font-semibold uppercase tracking-[0.18em]">
              Top ZIP codes
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="hammer-top-zips">
              <thead className="bg-white/5 text-zinc-400 uppercase text-[10px] tracking-[0.2em]">
                <tr>
                  <th className="text-left px-4 py-2">ZIP · Destination</th>
                  <th className="text-left px-4 py-2">Carriers</th>
                  <th className="text-right px-4 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_zips || []).map((z) => (
                  <tr key={z.zip} className="border-t border-white/5" data-testid={`hammer-zip-${z.zip}`}>
                    <td className="px-4 py-2.5">
                      <div className="text-white font-medium">{z.zip}</div>
                      <div className="text-[11px] text-zinc-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {z.city || '—'}{z.state ? `, ${z.state}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[11px] text-zinc-400">
                      {z.by_carrier && Object.keys(z.by_carrier).length > 0
                        ? Object.entries(z.by_carrier).map(([id, c]) => (
                            <div key={id} className="whitespace-nowrap">
                              <span className="text-zinc-300">{prettyCarrier(id)}</span>{' '}
                              <span className="text-amber-300 tabular-nums">{c}</span>
                            </div>
                          ))
                        : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right text-white font-semibold tabular-nums">{z.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recent activity feed */}
      <div className="mt-5 panel rounded-xl border border-white/10 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
          <Hammer className="w-4 h-4 text-amber-300" />
          <h3 className="font-display text-white text-sm font-semibold uppercase tracking-[0.18em]">
            Recent estimates
          </h3>
        </div>
        <ul className="divide-y divide-white/5 max-h-[420px] overflow-y-auto" data-testid="hammer-recent">
          {(data.recent || []).slice(0, 10).map((r, i) => (
            <li key={`${r.zip}-${r.created_at}-${i}`} className="px-4 py-2.5 flex items-baseline gap-3">
              <div className="w-1 h-8 rounded-full bg-amber-400/70 self-center shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium tabular-nums">
                  {fmtMoney(r.low)} <span className="text-zinc-600">–</span> {fmtMoney(r.high)}
                  <span className="text-zinc-500 font-normal ml-2 text-[11px] uppercase tracking-[0.14em]">
                    {prettyCarrier(r.carrier)}
                  </span>
                </div>
                <div className="text-[11px] text-zinc-500 truncate">
                  ZIP {r.zip}
                  {r.dest_city ? ` (${r.dest_city}${r.dest_state ? ', ' + r.dest_state : ''})` : ''}
                  {r.distance_miles != null ? ` · ${r.distance_miles} mi` : ''}
                  {r.zone != null ? ` · zone ${r.zone}` : ''}
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-[0.14em] shrink-0">
                {relTime(r.created_at)}
              </div>
            </li>
          ))}
          {(data.recent || []).length === 0 && (
            <li className="px-4 py-6 text-center text-zinc-500 text-sm">No recent estimates yet.</li>
          )}
        </ul>
      </div>

      {error && data && (
        <div className="mt-4 flex items-center gap-2 text-[11px] text-amber-300/80">
          <AlertCircle className="w-3.5 h-3.5" />
          Last refresh failed; showing cached data.
        </div>
      )}
    </div>
  );
}
